import { createSlice } from '@reduxjs/toolkit';
import {
  createDisponibilite,
  fetchAllDoctorSlots,
  fetchDoctorSlotsByDate,
  fetchDoctorAvailableSlotsByDate,
  updateDisponibilite,
  deleteDisponibilite,
  markRangeUnavailable,
  markRangeAvailable,
  markSlotsUnavailable,
  markSlotsAvailable,
} from "../thunks/disponibilityThunks";

const initialState = {
  slotsByDate: {},
  availableSlotsByDate: {},
  allSlots: [],
  loading: false,
  error: null,
    conflictingSlot: null,  // ← add if missing

  successMessage: null,
};

const normalizeSlotsPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.slots)) return payload.slots;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.slots)) return payload.data.slots;
  return [];
};

const disponibilitySlice = createSlice({
  name: 'disponibility',
  initialState,
  reducers: {
    clearDisponibilityError(state) {
      state.error = null;
    },
    clearDisponibilitySuccess(state) {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // create
      .addCase(createDisponibilite.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createDisponibilite.fulfilled, (state, action) => {
        state.loading = false;
        const createdSlots = normalizeSlotsPayload(action.payload);
        const createdIds = new Set(createdSlots.map((slot) => String(slot.id)));
        state.allSlots = [
          ...state.allSlots.filter((slot) => !createdIds.has(String(slot.id))),
          ...createdSlots,
        ];
        state.successMessage = 'Disponibilités créées avec succès';
      })
         .addCase(createDisponibilite.rejected, (state, action) => {
  state.loading = false;
  // Store only the message string — full payload is handled by the hook
  state.error = action.payload?.message || action.payload || 'Erreur lors de la création';
  state.conflictingSlot = action.payload?.conflictingSlot || null;
})

      // fetch all
      .addCase(fetchAllDoctorSlots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDoctorSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.allSlots = normalizeSlotsPayload(action.payload?.data);
      })
      .addCase(fetchAllDoctorSlots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur lors du chargement des créneaux';
      })

      // fetch by date
      .addCase(fetchDoctorSlotsByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorSlotsByDate.fulfilled, (state, action) => {
        state.loading = false;
        const { date, data } = action.payload;
        state.slotsByDate[date] = normalizeSlotsPayload(data);
      })
      .addCase(fetchDoctorSlotsByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur lors du chargement des créneaux';
      })

      // fetch available by date
      .addCase(fetchDoctorAvailableSlotsByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorAvailableSlotsByDate.fulfilled, (state, action) => {
        state.loading = false;
        const { date, data } = action.payload;
        state.availableSlotsByDate[date] = normalizeSlotsPayload(data);
      })
      .addCase(fetchDoctorAvailableSlotsByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur lors du chargement des créneaux disponibles';
      })

      // update
      .addCase(updateDisponibilite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDisponibilite.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSlot = action.payload?.data?.data ?? action.payload?.data;
        const updatedId = String(action.payload?.slotId ?? updatedSlot?.id ?? '');

        if (updatedSlot && updatedId) {
          const mergeUpdatedSlot = (slot) =>
            String(slot.id) === updatedId ? { ...slot, ...updatedSlot } : slot;

          state.allSlots = state.allSlots.map(mergeUpdatedSlot);
          Object.keys(state.slotsByDate).forEach((date) => {
            state.slotsByDate[date] = state.slotsByDate[date].map(mergeUpdatedSlot);
          });
          Object.keys(state.availableSlotsByDate).forEach((date) => {
            state.availableSlotsByDate[date] = state.availableSlotsByDate[date].map(mergeUpdatedSlot);
          });
        }
        state.successMessage = 'Créneau mis à jour avec succès';
      })
      .addCase(updateDisponibilite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur lors de la mise à jour';
      })

      // delete
      .addCase(deleteDisponibilite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDisponibilite.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = String(action.payload);

        state.allSlots = state.allSlots.filter(
          (slot) => String(slot.id) !== deletedId
        );

        Object.keys(state.slotsByDate).forEach((date) => {
          state.slotsByDate[date] = state.slotsByDate[date].filter(
            (slot) => String(slot.id) !== deletedId
          );
        });

        Object.keys(state.availableSlotsByDate).forEach((date) => {
          state.availableSlotsByDate[date] =
            state.availableSlotsByDate[date].filter(
              (slot) => String(slot.id) !== deletedId
            );
        });

        state.successMessage = 'Créneau supprimé avec succès';
      })
      .addCase(deleteDisponibilite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur lors de la suppression';
      })

      // mark/unmark
      .addCase(markRangeUnavailable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markRangeUnavailable.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Plage marquée indisponible';
      })
      .addCase(markRangeUnavailable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur';
      })

      .addCase(markRangeAvailable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markRangeAvailable.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Plage marquée disponible';
      })
      .addCase(markRangeAvailable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur';
      })

      .addCase(markSlotsUnavailable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markSlotsUnavailable.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Créneaux marqués indisponibles';
      })
      .addCase(markSlotsUnavailable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur';
      })

      .addCase(markSlotsAvailable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markSlotsAvailable.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Créneaux marqués disponibles';
      })
      .addCase(markSlotsAvailable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Erreur';
      });
  },
});

export const {
  clearDisponibilityError,
  clearDisponibilitySuccess,
} = disponibilitySlice.actions;

export default disponibilitySlice.reducer;